'use client'

import { useEffect, useState, type ReactNode } from 'react'

import { MaybeHtml } from '@/components/blocks/maybe-html'
import { SECTION_PADDING, SITE_CONTAINER } from '@/components/layout/container'
import { MotionPreset } from '@/components/ui/motion-preset'
import { SECTION_TITLE } from '@/lib/typography'
import { cn } from '@/lib/utils'

export type ValueSlide = {
  id: string
  icon: ReactNode
  title: string
  text?: string
}

/** Нэг үнэт зүйл дэлгэцэнд байх хугацаа (мс) */
const DURATION = 6000

/** Явцын мөрийг шинэчлэх алхам (мс) */
const TICK = 100

/**
 * ҮНЭТ ЗҮЙЛСИЙН ЭРГЭЛТ — `@ss-blocks/testimonials-component-23` блок дээр.
 *
 * Эх блокоос ХУУЛСАН: төвд нь том бичвэр, доор нь дарж болох эгнээ, идэвхтэй
 * нүдэн дээрх явцын зураас, `MotionPreset`-ийн солигдох гарц.
 *
 * ! ЭХ НЬ ХАРИЛЦАГЧИЙН СЭТГЭГДЭЛД зориулагдсан (аватар, компанийн лого).
 * ! Үнэт зүйлд хүн ч, компани ч байхгүй тул:
 * !   • аватарын оронд үнэт зүйлийн ДҮРС, нэрний оронд ГАРЧИГ
 * !   • доод эгнээний логоны оронд дүрс + гарчиг
 * !   • бичвэр нь CMS-ийн rich text байж болох тул `MaybeHtml`
 */
const ValuesRotator = ({
  items,
  eyebrow,
  heading
}: {
  items: ValueSlide[]
  eyebrow?: string | null
  heading?: string | null
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setProgress(prev => Math.min(prev + (100 * TICK) / DURATION, 100)), TICK)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (progress < 100) return

    const timeout = setTimeout(() => {
      setActiveIndex(current => (current + 1) % items.length)
      setProgress(0)
    }, TICK)

    return () => clearTimeout(timeout)
  }, [progress, items.length])

  if (items.length === 0) {
    return null
  }

  const active = items[activeIndex]

  return (
    <section className={SECTION_PADDING}>
      <div className={SITE_CONTAINER}>
        <div className='flex flex-col items-center overflow-hidden rounded-3xl border'>
          <div className='flex w-full max-w-4xl flex-col items-center gap-8 px-4 py-12 text-center md:px-6 lg:py-16'>
            {(eyebrow || heading) && (
              <div className='flex flex-col items-center gap-3'>
                {eyebrow && (
                  <span className='text-muted-foreground inline-flex items-center gap-2.5 text-xl font-medium tracking-widest uppercase'>
                    <span className='bg-primary size-2.5 rounded-full' aria-hidden='true' />
                    {eyebrow}
                  </span>
                )}
                {heading && <h2 className={SECTION_TITLE}>{heading}</h2>}
              </div>
            )}

            {/*
              Идэвхтэй үнэт зүйлийн бичвэр.
              ! ГОЛЛОЖ, ТОД ӨНГӨӨР. `RichContent`-ийн суурь нь `text-justify` +
              ! бүдэг өнгө (урт нийтлэлд зориулсан) — ишлэл маягийн голлосон
              ! бичвэрт үгсийн хооронд том завсар гаргаж байв.
            */}
            <MotionPreset
              key={`text-${activeIndex}`}
              fade
              blur
              slide={{ direction: 'down', offset: 30 }}
              transition={{ duration: 0.5 }}
            >
              <MaybeHtml
                text={active.text}
                className='text-foreground text-center text-2xl leading-relaxed font-medium text-balance hyphens-none md:text-3xl'
              />
            </MotionPreset>

            <MotionPreset
              key={`title-${activeIndex}`}
              fade
              blur
              delay={0.3}
              slide={{ direction: 'down', offset: 30 }}
              transition={{ duration: 0.5 }}
              className='flex items-center gap-3'
            >
              <span className='bg-primary/10 text-primary flex size-12 items-center justify-center rounded-full [&>svg]:size-6'>
                {active.icon}
              </span>
              <span className='text-xl font-semibold'>{active.title}</span>
            </MotionPreset>
          </div>

          {/* Доод эгнээ — дарж шууд сонгоно */}
          <div className='w-full max-lg:overflow-x-auto'>
            <div className='flex min-w-180'>
              {items.map((item, index) => {
                const isActive = index === activeIndex

                return (
                  <button
                    key={item.id}
                    type='button'
                    aria-label={item.title}
                    aria-current={isActive}
                    onClick={() => {
                      setActiveIndex(index)
                      setProgress(0)
                    }}
                    className={cn(
                      'hover:bg-muted/60 relative flex flex-1 cursor-pointer flex-col items-center gap-2 border-t p-5 transition-colors not-last:border-r',
                      !isActive && 'opacity-60'
                    )}
                  >
                    <span className='[&>svg]:size-6'>{item.icon}</span>
                    <span className='text-xl font-medium'>{item.title}</span>

                    {isActive && (
                      <span
                        className='bg-primary absolute inset-x-0 top-0 left-0 h-0.5 transition-all duration-100 ease-linear'
                        style={{ width: `${progress}%` }}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ValuesRotator
